import { AxiosResponse } from 'axios';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { format } from 'date-fns';
import camelize from '@/tools/camelize';
import {
  ACTION_FETCH,
  SET_SHEDULE,
  SET_CHILDREN,
  REMOVE_CHILDREN,
  ADD_CHILDREN,
  SEND_ACTIVITY_ORDER,
  SET_ACTIVITIES,
  SET_TIME_PERIODS_BY_DATES,
} from './const';
import {
  ActivityExtended,
  Activity,
  Params,
  Activities,
  Providers,
  RootState,
  State,
  Statuses,
  SheduleParams,
  Children,
  Shedule,
  ITimePeriodsByDates,
  ITimePeriodsByDatesItem,
} from './types';

const { ERROR, READY, WAITING, SENDING, SENT } = Statuses;

const intialize = (): Activity => ({
  announce: '',
  name: '',
  conditions: '',
  contacts: '',
  cost: '',
  description: '',
  duration: '',
  educationForm: '',
  educationFormName: '',
  expectedResults: '',
  id: '',
  imageSrc: '',
  images: [],
  isLocked: true,
  level: '',
  location: '',
  materialBase: '',
  members: [],
  municipalityId: '',
  partnerId: '',
  periodStart: '',
  persons: '',
  plan: '',
  press: '',
  results: '',
  section: '',
  state: '',
  targets: '',
  partnerName: '',
  partnerShortName: '',
  partnerPhone: '',
  partnerEmail: '',
});

const FETCH_ACTIVITY = 'fetchActivity';
const FETCH_SHEDULE = 'fetchShedule';
const FETCH_ACTIVITIES = 'fetchActivities';
const SET_ACTIVITY = 'setActivity';
const SET_STATUS = 'setStatus';

const storeModule = ({ $api, $config }: Providers) => {
  @Module({
    namespaced: true,
    stateFactory: true,
  })
  class PageOrganizationStore extends VuexModule<State, RootState> {
    // State
    activity: Activity = intialize();
    activities: Activities = [];
    children: Children = [];
    shedule: Shedule = [];
    timePeriodsByDates: ITimePeriodsByDates = {};
    status: Statuses = READY;
    srvErrorMsg = null;

    // Getters
    get full(): ActivityExtended {
      const {
        activityLevels,
        activityMembers,
        educationForms,
        municipalities,
      } = this.context.rootState;
      const { activity } = this;
      const level = activityLevels.find(
        ({ id }) => String(id) === String(activity.level)
      );
      const educationForm = educationForms.find(
        ({ id }) => String(id) === String(activity.educationForm)
      );
      const educationFormName = activity.educationFormName;
      const municipality = municipalities.find(
        ({ id }) => String(id) === String(activity.municipalityId)
      );
      const members = activity.members
        .map((member) => {
          const founded = activityMembers.find(
            ({ id }) => String(id) === String(member)
          );
          return founded ? founded.name : false;
        })
        .filter((member) => member);
      const result = {
        ...activity,
        members,
        municipality,
        partnerName: activity.partnerName,
        partnerPhone: activity.partnerPhone,
        partnerEmail: activity.partnerEmail,
      };
      if (educationForm) result.educationForm = educationForm.name;
      if (educationFormName) result.educationFormName = educationFormName;
      if (level) result.level = level.name;
      return result;
    }

    // Mutations
    @Mutation
    [SET_ACTIVITY](organization: Activity) {
      const blank = intialize();
      this.activity = {
        ...blank,
        ...organization,
      };
    }

    @Mutation
    [SET_SHEDULE](shedule: Array<ITimePeriodsByDatesItem>) {
      const dateList = shedule.map(({ startDate }: SheduleParams) =>
        format(startDate, 'YYYY-MM-DD')
      );

      const timesByDates: ITimePeriodsByDates = shedule.reduce(
        (acc: any, cur: any) => {
          const key = format(cur.startDate, 'YYYY-MM-DD');
          acc[key]
            ? acc[key].push({
                ...cur,
                timePeriodLabel: `${format(cur.startDate, 'HH:mm')} - ${format(
                  cur.endDate.toString(),
                  'HH:mm'
                )}`,
              })
            : (acc[key] = [
                {
                  ...cur,
                  timePeriodLabel: `${format(
                    cur.startDate,
                    'HH:mm'
                  )} - ${format(cur.endDate.toString(), 'HH:mm')}`,
                },
              ]);
          return acc;
        },
        {}
      );
      this.shedule = dateList;
      this.timePeriodsByDates = timesByDates;
    }

    @Mutation
    [SET_CHILDREN](children: Children) {
      this.children = children;
    }

    @Mutation
    [REMOVE_CHILDREN](index: any) {
      this.children.splice(index, 1);
    }

    @Mutation
    [ADD_CHILDREN](value: any) {
      this.children.push(...value);
    }

    @Mutation
    [SET_ACTIVITIES](payload: Activities = []) {
      this.activities = payload.map((item) => <Activity>camelize(item));
    }

    @Mutation
    [SET_STATUS](status: Statuses) {
      this.status = status;
    }
    @Mutation
    setSrvErrorMsg(msg: any) {
      this.srvErrorMsg = msg;
    }

    // Actions
    @Action
    async [ACTION_FETCH](params: Params) {
      const { commit, dispatch } = this.context;
      // if (this.status === WAITING) return;
      try {
        commit(SET_STATUS, WAITING);
        const activity: Promise<void> = dispatch(FETCH_ACTIVITY, params);
        const activities: Promise<void> = dispatch(FETCH_ACTIVITIES, params);
        const shedule: Promise<void> = dispatch(FETCH_SHEDULE, params);
        await Promise.all([activity, activities, shedule]);
        commit(SET_STATUS, READY);
      } catch (e) {
        // Обрабатываем ошибку
        commit(SET_STATUS, ERROR);
      }
    }

    @Action({ commit: SET_ACTIVITY })
    async [FETCH_ACTIVITY]({ id }: Params) {
      const { data } = await $api.get<AxiosResponse<Activity>>(
        `activity/get/${id}`
      );
      return camelize(data.data);
    }

    @Action({ commit: SET_SHEDULE })
    async [FETCH_SHEDULE]({ id }: Params) {
      const today = new Date();
      const { data } = await $api.get<AxiosResponse<any>>(
        'activity/order/shedule',
        {
          params: {
            event_id: id,
            start_date: format(today, 'YYYY-MM-DD'),
            end_date: format(
              today.setFullYear(today.getFullYear() + 1),
              'YYYY-MM-DD'
            ),
          },
        }
      );
      return data.data;
    }

    @Action({ commit: SET_ACTIVITIES })
    async [FETCH_ACTIVITIES]({ id }: Params) {
      const params = {
        organizer: id,
        limit:
          ($config.activities &&
            $config.activities.card &&
            $config.activities.card.programsCount) ||
          4,
      };
      const { data } = await $api.get<AxiosResponse<Activities>>(
        'activities/get',
        { params }
      );
      return data.data;
    }

    @Action
    async [SEND_ACTIVITY_ORDER](data: any) {
      const { commit } = this.context;
      commit(SET_STATUS, SENDING);
      try {
        await $api.post<AxiosResponse<any>>('activity/order/create', data);
        commit(SET_STATUS, SENT);
        commit('setSrvErrorMsg', null);
        return {
          type: 'success',
        };
      } catch (e) {
        //commit(SET_STATUS, ERROR);  оно типа в 404 залетает, нам эт не нужно тут
        commit(SET_STATUS, SENT);
        commit('setSrvErrorMsg', e.response.data.message || null);
        return {
          type: 'error',
          text: {
            ...(e.response?.data?.message && {
              description: e.response.data.message,
            }),
          },
        };
      }
    }
  }

  return PageOrganizationStore;
};

export default storeModule;



// WEBPACK FOOTER //
// ./src/pages/activities/card/store.ts